import EditProfile from "./edit_profile";
import ProfilePreview from "./preview_profile";
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import ProfileMenu from "../../components/profile/profile_menu";
import { useNavigate, useLocation } from "react-router-dom";
import Banner from "../../components/Banner";
import MatchRequestNotification from "../../components/notifications/MatchRequestNotification";
import InterviewRequestAlert from "../../components/notifications/InterviewRequestAlert";
import SaveNotification from "../../components/notification/save_notification";
import HelpPanel from "../../components/HelpPanel";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";



const Profile = () => {
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState("yes");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSection, setSelectedSection] = useState(
    "1. General Information"
  );
  const [candidateNeedUpdate, setCandidateNeedUpdate] = useState(true);
  const [missingFields, setMissingFields] = useState([]);
  
  // State for match request notifications
  const [showMatchRequestModal, setShowMatchRequestModal] = useState(false);
  const [matchRequestNotification, setMatchRequestNotification] = useState({ show: false, message: '', type: '' });



  const { isAuthenticated, isChecking } = useAuthRedirect();

  // Define required fields
  const requiredFields = [
    "cv",
    "linkedin",
    "name",
    "lastName",
    "phone_number",
    "work_experience_1_role",
    "work_experience_1_company",
    "work_experience_1_description",
    "work_experience_2_role",
    "work_experience_2_company",
    "work_experience_2_description",
    "top_achievement",
    "minimum_salary",
    "ideal_salary",
    "right_to_work",
    "roles",
  ];

  // Update missing fields whenever candidate changes
  useEffect(() => {
    if (candidate !== "yes") {
      const missing = requiredFields.filter((field) => !candidate[field]);
      setMissingFields(missing);
    }
  }, [candidate]);

  const transformRoles = (roles) => {
    const roleSet = new Set(roles);
    if (roleSet.size === 1) {
      if (roleSet.has("Full Stack Developer")) return ["Full Stack"];
      if (roleSet.has("Front End Developer")) return ["Front End"];
      if (roleSet.has("Back End Developer")) return ["Back End"];
    }
    if (
      roleSet.has("Front End Developer") &&
      roleSet.has("Back End Developer")
    ) {
      return ["Full Stack"];
    }
    if (roleSet.has("Full Stack Developer")) {
      return ["Full Stack"];
    }
    return roles;
  };

  useEffect(() => {
    if (!isAuthenticated || isChecking) return;
    
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`/api/candidates/fetchCandidate`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const candidate = response.data.candidate;

        let sweRoles = candidate.fields["SWE relevant roles"];
        if (sweRoles) {
          sweRoles = Array.isArray(sweRoles)
            ? sweRoles[0].split(",").map((role) => role.trim())
            : sweRoles.split(",").map((role) => role.trim());
          sweRoles = transformRoles(sweRoles);
        } else {
          sweRoles = []; // Ensure it's an empty array if not present
        }

        const transformedData = {
          stream: candidate.fields["Stream (from onboarding form) NEW"] || "",
          id: candidate["id"],
          url_id: candidate.fields["ID"] || "",
          createdTime: candidate["createdTime"] || "", // Ensure this is correctly fetched
          years_of_exp:
            candidate.fields["Years of exp (or commercial exp)"] || "",
          cv: candidate.fields["CV RAW"] || "",
          video: candidate.fields["Video intro (new)"] || "",
          linkedin: candidate.fields["LinkedIn RAW"] || "",
          name: candidate.fields["Name"] || "",
          lastName: candidate.fields["Surname"] || "",
          email: candidate.fields["Email"] || "",
          phone_number: candidate.fields["Phone number"] || "",
          undergrad_degree_type:
            candidate.fields["Undergrad degree type"] || "",
          undergrad_degree_subject: candidate.fields["Undergrad subject"] || "",
          undergrad_university: candidate.fields["Undergrad university"] || "",
          undergrad_graduation_year:
            candidate.fields["Undergrad graduation year"] || "",
          undergrad_result: candidate.fields["Undergrad result"] || "",
          postgrad_degree_type: candidate.fields["Postgrad degree type"] || "",
          postgrad_degree_subject: candidate.fields["Postgrad subject"] || "",
          postgrad_university: candidate.fields["Postgrad university"] || "",
          postgrad_graduation_year:
            candidate.fields["Postgrad graduation year"] || "",
          postgrad_result: candidate.fields["Postgrad result"] || "",
          years_of_exp:
            candidate.fields["Years of exp (or commercial exp)"] || "",
          industry_experience: candidate.fields["Industry experience"] || "",
          time_spent_coding: candidate.fields["Time spent coding"] || "",
          coding_languages: candidate.fields["Coding languages new"] || "",
          work_experience_1_role:
            candidate.fields["(1) Work Experience Role"] || "",
          work_experience_1_company:
            candidate.fields["(1) Work Experience Company"] || "",
          work_experience_1_description:
            candidate.fields["(1) Work Experience Description"] || "",
          work_experience_1_duration_yrs:
            candidate.fields["(1) Time spent in work experience YRS"],
          work_experience_1_duration_mos:
            candidate.fields["(1) Time spent in work experience MOS"],
          work_experience_2_role:
            candidate.fields["(2) Work Experience Role"] || "",
          work_experience_2_company:
            candidate.fields["(2) Work Experience Company"] || "",
          work_experience_2_description:
            candidate.fields["(2) Work Experience Description"] || "",
          work_experience_2_duration_yrs:
            candidate.fields["(2) Time spent in work experience YRS"],
          work_experience_2_duration_mos:
            candidate.fields["(2) Time spent in work experience MOS"],
          most_impressive_swe:
            candidate.fields["Most impressive thing SWE"] || "",
          github: candidate.fields["Github RAW"] || "",
          portfolio_swe: candidate.fields["SWE Portfolio"] || "",
          marketing_portfolio: candidate.fields["Marketing portfolio RAW"] || "",
          top_achievement: candidate.fields["Top achievement"] || "",
          backup_achievement: candidate.fields["Backup achievement"] || "",
          proficient_technologies:
            candidate.fields["Proficient technologies copy"] || "",
          recent_target: candidate.fields["Recent target achieved"] || "",
          sales_experience: candidate.fields["Sales experience - skills"] || "",
          english_proficiency:
            candidate.fields["English proficiency swe"] || "",
          languages: candidate.fields["Languages copy"] || "",
          roles: candidate.fields["Generalist roles suitable for copy"] || "",
          industries_interested:
            candidate.fields["Industries most interested in copy"] || "",
          open_to_any_industries:
            candidate.fields["Open to any industries"] || "",
          industries_not_interested:
            candidate.fields["Industries NOT interested in"] || "",
          role_preferences_context:
            candidate.fields["Additional context on next role preferences"] ||
            "",
          working_style: candidate.fields["Preferred working style copy"] || "",
          open_to_relocation: candidate.fields["Open to relocation"] || "",
          minimum_salary: candidate.fields["Minimum salary"] || "",
          ideal_salary: candidate.fields["Ideal salary"] || "",
          commission_expectation:
            candidate.fields["Commission expectation (sales only)"] || "",
          salary_context: candidate.fields["Salary context"] || "",
          image: candidate.fields.Photo ? candidate.fields.Photo[0].url : null,
          work: candidate.fields["Raw work experience"] || "",
          swe_roles: sweRoles,
          description: candidate.fields["Top achievement"] || "",
          mostRecentCohort: candidate.fields["Most recent cohort"] || "",
          recentTarget: candidate.fields["Recent target achieved"] || "",
          sales:
            candidate.fields[
              "Proven Sales Experience / High Potential for Sales"
            ] || "",
          right_to_work: candidate.fields["RTW doc"] || "",
        };

        setCandidate(transformedData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [candidateNeedUpdate, isAuthenticated, isChecking]);

  // Handle match request action completion
  const handleMatchRequestActionComplete = (action, roleTitle) => {
    setMatchRequestNotification({
      show: true,
      message: action === 'accepted' 
        ? `✅ Great! You've accepted the interview request for ${roleTitle}. Check your email for next steps.`
        : `❌ You've declined the interview request for ${roleTitle}.`,
      type: action === 'accepted' ? 'success' : 'info'
    });
    setTimeout(() => setMatchRequestNotification({ show: false, message: '', type: '' }), 4000);
  };

  return (
    <div className="min-h-screen bg-white pt-10">
      {/* Interview Request Alert - floating notification */}
      <InterviewRequestAlert onOpenModal={() => setShowMatchRequestModal(true)} />
      
      {/* Match Request Modal */}
      {showMatchRequestModal && (
        <MatchRequestNotification
          onClose={() => setShowMatchRequestModal(false)}
          onActionComplete={handleMatchRequestActionComplete}
        />
      )}
      
      {/* Match Request Action Notification */}
      <SaveNotification 
        show={matchRequestNotification.show}
        message={matchRequestNotification.message}
        type={matchRequestNotification.type}
      />
      
      {/* Main content without header */}
      <div className="bg-white">
        {/* Horizontal Menu */}
        <ProfileMenu
          setSelectedSection={setSelectedSection}
          selectedSection={selectedSection}
          missingFields={missingFields}
        />
        
        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 bg-white lg:max-w-[65%] w-full order-1">
              <EditProfile
                candidate={candidate}
                setCandidate={setCandidate}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
                candidateNeedUpdate={candidateNeedUpdate}
                setCandidateNeedUpdate={setCandidateNeedUpdate}
              />
            </div>
            <div className="w-full lg:w-[35%] order-2 mb-8 lg:mb-0">
              <ProfilePreview candidate={candidate} loading={loading} />
            </div>
          </div>
        </div>
      </div>

      {/* Help Panel */}
      <HelpPanel candidateStream={candidate !== "yes" ? candidate.stream : ""} />
      

    </div>
  );
};

export default Profile;
